import React from 'react';
import {
  OTHER_CASE_TYPES_LABEL,
  minimumRangeText,
  otherCaseTypesMinimum,
  serviceCaseRows,
  serviceMinimumRange,
} from '../Provider/Schedule/serviceCaseTypes';
import { alsoAnnual, annualText } from '../Provider/Schedule/serviceRateTypes';
import WeekdayStrip from './WeekdayStrip';
import './ProfileScheduleStrip.css';

// The per-schedule block shown INSIDE the anonymous provider card. Rendered on
// both the provider-side preview and (Phase 2) the /rm recruiter deck, so it
// must stay presentational and depend only on the `schedule` prop shape from
// composeAnonCard.scheduleBlock / the recruiter-matches payload.
// Cadence and shift words, shared with the services page rows so a row and
// its card say the same thing.
export const CADENCE_LABELS = { w: 'Every week', eo: 'Every other week', f13: '1st and 3rd weeks', f24: '2nd and 4th weeks', e3: 'Every third week' };
export const SHIFT_TAG_LABELS = { days: 'Day shifts', nights: 'Night shifts', call: 'On-call', weekends: 'Weekends', evenings: 'Evening shifts' };
const CAD = CADENCE_LABELS;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Space on the card is tight, so the year only appears when it isn't this
// year -- exactly when omitting it turns "Available Jul 28 to Jan 15" into a
// guess about which January.
const fmt = (iso, nowYear = new Date().getFullYear()) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const base = `${MONTHS[Number(m) - 1]} ${Number(d)}`;
  return Number(y) === nowYear ? base : `${base}, ${Number(y)}`;
};

export const formatServiceDate = (iso) => fmt(iso, -1);
export const formatServiceWindow = (schedule = {}) => {
  if (!schedule?.from) return 'Dates to be added';
  return schedule.to
    ? `${formatServiceDate(schedule.from)} – ${formatServiceDate(schedule.to)}`
    : `From ${formatServiceDate(schedule.from)}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// Whether a service has started. `today` is an ISO date, injectable for tests.
export const serviceAvailableNow = (schedule, today = todayIso()) =>
  !!schedule?.from && schedule.from <= today;

// The status the services page row and this card both print for a service:
// "Available now" once it has started, else "Available from {date}".
export const serviceStatusLabel = (schedule, today = todayIso()) => {
  if (!schedule?.from) return null;
  return serviceAvailableNow(schedule, today)
    ? 'Available now'
    : `Available from ${fmt(schedule.from)}`;
};

export default function ProfileScheduleStrip({ schedule, presentation = 'card', rateCaption }) {
  if (!schedule) return null;
  const days = new Set(schedule.daysOfWeek || []);
  const windowText = schedule.to
    ? `Available ${fmt(schedule.from)} to ${fmt(schedule.to)}`
    : serviceStatusLabel(schedule);
  // A floor the provider has not stated is not a floor. Interpolating it raw
  // renders "$null/hr" (and the salary branch a fabricated "$0/yr"), so the
  // whole block sits out until there is a real number to show. A live schedule
  // always has one -- the go-live guard requires it -- so this only ever hides
  // the block on an unfinished draft, never on the recruiter deck.
  const salary = Number(schedule.minSalary);
  const hourly = Number(schedule.minHourlyRate);
  const isSalary = schedule.rateType === 'salary';
  // The provider set rateVisibility 'private'. The backend has already nulled
  // both amounts, so hasFloor would be false and the pay block would simply
  // VANISH -- which reads as "this provider never stated a rate", the opposite
  // of what is true and the opposite of what the gate will tell a recruiter who
  // submits a role. So a withheld floor gets its own explicit state, the same
  // withheld-is-not-absent distinction the listing card already draws.
  const rateWithheld = !!schedule.rateWithheld;
  const hasFloor =
    !rateWithheld &&
    (isSalary
      ? Number.isFinite(salary) && salary > 0
      : Number.isFinite(hourly) && hourly > 0);
  const floor = isSalary ? `$${salary.toLocaleString('en-US')}/yr` : `$${hourly}/hr`;
  // A service that publishes an annual floor beside its hourly one prints both
  // on its pay line, hourly first: "$290/hr or $340,000/yr". Null for a salary
  // service, a withheld rate and a service with one floor. The Required line
  // below names the same floors, and the caption and label that name the
  // minimum say "minimums" and "Minimum rates" beside two figures.
  const annual = !isSalary ? alsoAnnual(schedule) : null;
  const withAnnual = (text) => (annual != null ? `${text} or ${annualText(annual)}` : text);
  // The case types this service covers, each at the minimum a listing for it
  // is checked against (its own, or the service minimum when blank), plus
  // General at the service minimum, highest first: the rows the services page
  // prints. Where no figure may print (rate withheld, a salary service, a
  // draft with no floor yet) they are names only, and General drops out
  // because it would only restate the hidden floor. serviceCaseRows reads
  // rateWithheld itself: the composers null the minimums first, this is the
  // second lock.
  const caseRows = serviceCaseRows(schedule);
  // Once a service has effective case rows, ITS floor is no longer one
  // number: a listing can be checked against a case minimum below the
  // service minimum (services spec 2026-09-19, section 3). The pay block
  // then reads "by case type" from the low end of serviceMinimumRange
  // instead of the plain service minimum, which used to sit next to a case
  // row that read lower and contradict it. Unchanged for a withheld rate, a
  // salary service (case minimums are hourly-only), or a service with no
  // case rows.
  const hasCaseFloor = hasFloor && !isSalary && caseRows.length > 0;
  const caseFloorRange = hasCaseFloor ? serviceMinimumRange(schedule) : null;
  const caseFloorValue = minimumRangeText(caseFloorRange, '/hr');
  // The enforced terms, advertised where recruiters read the card. Pay is
  // unconditionally a floor; strictMinimums now only decides which additional
  // terms (currently contract length) are hard constraints. The recruiter-side
  // gate holds a role to the floor of its own unit (an hourly role to the
  // hourly floor, a salaried one to the annual floor), so the pay part names
  // every floor the card prints: "$290/hr", "$340,000/yr" or both. A
  // case-floor service drops it: no single hourly figure is what every listing
  // on it is required to meet, and an annual figure alone would read as its
  // only pay term. A withheld rate has no figure to name either.
  const strict = new Set(schedule.strictMinimums || []);
  const requiredParts = [];
  if (hasFloor && !hasCaseFloor) requiredParts.push(withAnnual(floor));
  if (strict.has('contractWeeks') && schedule.minContractWeeks) {
    requiredParts.push(`${schedule.minContractWeeks}-wk blocks`);
  }
  // A pay part that already holds an "or" takes a comma before the "and" that
  // follows it: "$290/hr or $340,000/yr, and 13-wk blocks" reads as two terms.
  const requiredJoin = requiredParts.some((part) => part.includes(' or ')) ? ', and ' : ' and ';
  const caseTypes = (caseRows.some((row) => row.minHourlyRate != null)
    ? caseRows
    : caseRows.filter((row) => row.caseType !== 'general')
  ).map((row) => ({
    key: row.caseType,
    label: row.label,
    rate: row.minHourlyRate != null ? `$${row.minHourlyRate}/hr` : null,
  }));
  // Case types the service does not list are taken too, checked against its
  // highest minimum; said last, with the figure only where figures print.
  if (caseRows.length) {
    const other = otherCaseTypesMinimum(schedule);
    caseTypes.push({
      key: 'other',
      label: OTHER_CASE_TYPES_LABEL,
      rate: other != null ? `$${other}/hr` : null,
    });
  }
  const tags = [
    ...(schedule.shiftTags || []).map((tag) => SHIFT_TAG_LABELS[tag] || tag),
    ...[...(schedule.shiftLengths || [])].sort((a, b) => a - b).map((l) => `${l} hr`),
  ];
  if (presentation === 'service') {
    const serviceTags = [
      ...(schedule.shiftTags || []).map((tag) => SHIFT_TAG_LABELS[tag] || tag),
      ...[...(schedule.shiftLengths || [])].sort((a, b) => a - b).map((length) => `${length} hours`),
    ];
    const dayNames = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [0, 'Sun']];
    const weekdays = days.size === 5 && [1, 2, 3, 4, 5].every((d) => days.has(d));
    const dayLabel = weekdays ? 'Mon–Fri' : dayNames.filter(([d]) => days.has(d)).map(([, label]) => label).join(', ');
    return (
      <div className="crna-service-schedule" data-testid="profile-schedule-strip">
        <div className="crna-service-window">{formatServiceWindow(schedule)}</div>
        <div className="crna-service-rhythm">
          {[dayLabel, CAD[schedule.cadence], serviceTags.join(' · ')].filter(Boolean).map((value, i) => <span key={i}>{value}</span>)}
        </div>
        {schedule.preferredContractWeeks ? <p className="crna-service-blocks">Open to {schedule.preferredContractWeeks}-week blocks</p> : null}
        {caseTypes.length ? (
          <div className="crna-service-cases" data-testid="service-case-types">
            <span className="crna-service-cases__label">Case types</span>
            <ul className="crna-service-cases__list">
              {caseTypes.map((row) => (
                <li key={row.key} className="crna-service-case">
                  {row.label}
                  {row.rate ? <span className="crna-service-case__rate">{row.rate} min</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {(hasFloor || rateWithheld) && (
          <div className="crna-service-rate">
            <strong>
              {rateWithheld
                ? 'Competitive Rate'
                : withAnnual(hasCaseFloor ? caseFloorValue : floor)}
            </strong>
            <span>
              {rateCaption ||
                (rateWithheld
                  ? 'The provider’s minimum still applies'
                  : hasCaseFloor
                    ? 'Minimum by case type'
                    : annual != null
                      ? 'Minimum rates'
                      : 'Minimum rate')}
            </span>
          </div>
        )}
        {strict.has('contractWeeks') && schedule.minContractWeeks ? <p className="crna-service-blocks">Requires {schedule.minContractWeeks}-week blocks</p> : null}
      </div>
    );
  }
  return (
    <div className="pv-anon-sched" data-testid="profile-schedule-strip">
      <div className="pv-anon-sched__avail">{windowText}</div>
      {schedule.preferredContractWeeks ? (
        <div className="pv-anon-sched__blocks">Open to {schedule.preferredContractWeeks}-week blocks</div>
      ) : null}
      <WeekdayStrip days={schedule.daysOfWeek} />
      <div className="pv-anon-sched__cadence">{CAD[schedule.cadence] || ''}</div>
      <div className="pv-anon-sched__tags">
        {tags.map((t, i) => <span key={i} className="pv-anon-sched__tag">{t}</span>)}
      </div>
      {caseTypes.length ? (
        <div className="pv-anon-sched__cases" data-testid="sched-case-types">
          <div className="pv-anon-sched__floorlbl">Case types</div>
          <div className="pv-anon-sched__tags">
            {caseTypes.map((row) => (
              <span key={row.key} className="pv-anon-sched__tag pv-anon-sched__tag--case">
                {row.label}
                {row.rate ? <span className="pv-anon-sched__caserate"> · {row.rate}</span> : null}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {rateWithheld ? (
        <>
          <div className="pv-anon-sched__floorlbl">Pay floor</div>
          <div
            className="pv-anon-sched__floor pv-anon-sched__floor--private"
            data-testid="sched-floor-private"
          >
            Competitive Rate
          </div>
          <div className="pv-anon-sched__floorcap">
            {rateCaption || 'Recruiters have to meet it to reach this provider.'}
          </div>
        </>
      ) : hasFloor ? (
        <>
          <div className="pv-anon-sched__floorlbl">
            {hasCaseFloor ? 'Pay floor by case type' : 'Pay floor'}
          </div>
          <div className="pv-anon-sched__floor">
            {withAnnual(hasCaseFloor ? caseFloorValue : floor)}
          </div>
          <div className="pv-anon-sched__floorcap">
            {rateCaption ||
              (hasCaseFloor || annual != null
                ? 'This provider’s own minimums, not a quote.'
                : "This provider's own minimum, not a quote.")}
          </div>
        </>
      ) : null}
      {requiredParts.length ? (
        <div className="pv-anon-sched__required" data-testid="sched-required-line">
          Required: {requiredParts.join(requiredJoin)}
        </div>
      ) : null}
    </div>
  );
}
